import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { AppButton, Box, FormProvider, Preloader, ShimmerBox, Text } from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { DEFAULT_DATE_FORMAT } from '@/common/constants';
import ROUTES from '@/common/routes';
import ScanPreviewContainer from '@/components/controls/DropScan/ScanPreview/ScanPreviewContainer';
import useFilesInDocumentStore from '@/components/controls/DropScan/useFilesInDocument.store';
import { ICreateDocumentAggregatorDtoDocumentTypeEnum } from '@/dtos';
import { SnackbarState } from '@/modules/core/core.types';
import { useSnackbar } from '@/modules/core/hooks';
import useLettersOfMedicalNecessityForm, {
  LettersOfMedicalNecessityFormField,
} from '@/modules/MyDocuments/hooks/useLettersOfMedicalNecessityForm';
import { useViewLmnItem } from '@/modules/MyDocuments/hooks/useViewLmnItem';
import { LMN_UPLOAD_STORE_KEY, LMNPopupMode } from '@/modules/MyDocuments/myDocuments.types';
import useUpdateLmnItemQuery from '@/modules/MyDocuments/queries/useUpdateLmnItem.query';

import ChangeFile from '../ChangeFile/ChangeFile';

import DocumentPreviewPopupForm from './LettersOfMedicalNecessityPopup/DocumentPreviewPopup/DocumentPreviewPopupForm';

interface Props {
  mode?: LMNPopupMode;
}

const LettersOfMedicalNecessityViewPage: React.FC<Props> = ({ mode = LMNPopupMode.VIEW }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { itemId } = useParams<{ itemId: string }>();
  const { methods } = useLettersOfMedicalNecessityForm({ isViewMode: mode === LMNPopupMode.VIEW });
  const { values, handleReset } = useFilesInDocumentStore(LMN_UPLOAD_STORE_KEY);
  const { updateLMN } = useUpdateLmnItemQuery();
  const { handleAddPermanentSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);

  const { lmnItem, isLoading: isViewLoading } = useViewLmnItem({ id: itemId });

  useEffect(() => {
    return () => {
      handleReset();
    };
  }, [handleReset]);

  const allFiles = useMemo(() => (values?.files?.length ? values?.files : []), [values?.files]);

  const handleClose = useCallback(() => {
    history.push(ROUTES.MY_DOCUMENTS_LMN);
  }, [history]);

  const handleEdit = useCallback(() => {
    history.push(ROUTES.MY_DOCUMENTS_LMN_EDIT_MOBILE(itemId));
  }, [history, itemId]);

  const handleSave = useCallback(async () => {
    if (!itemId) return;
    try {
      setIsLoading(true);
      const formValues = methods.getValues();
      await updateLMN({
        id: itemId,
        description: formValues[LettersOfMedicalNecessityFormField.description],
        name: formValues[LettersOfMedicalNecessityFormField.name],
      });
      handleAddPermanentSnackbar({
        text: t('Letter of Medical Necessity was updated.'),
        state: SnackbarState.positive,
        closeIcon: true,
        autoRemove: true,
      });
      history.push(ROUTES.MY_DOCUMENTS_LMN);
    } finally {
      setIsLoading(false);
    }
  }, [itemId, methods, updateLMN, handleAddPermanentSnackbar, t, history]);

  const validDates = useMemo(() => {
    if (!lmnItem?.validFrom || !lmnItem?.validTo) return '';
    const fromDate = dayjs(lmnItem.validFrom).format(DEFAULT_DATE_FORMAT);
    const toDate = dayjs(lmnItem.validTo).format(DEFAULT_DATE_FORMAT);
    return `${fromDate} - ${toDate}`;
  }, [lmnItem?.validFrom, lmnItem?.validTo]);

  useEffect(() => {
    if (lmnItem) {
      methods.setValue(
        LettersOfMedicalNecessityFormField.type,
        ICreateDocumentAggregatorDtoDocumentTypeEnum.LETTER_OF_MEDICAL_NECESSITY
      );
      methods.setValue(LettersOfMedicalNecessityFormField.name, lmnItem?.name || '');
      methods.setValue(LettersOfMedicalNecessityFormField.description, lmnItem?.description || '');
      methods.setValue(LettersOfMedicalNecessityFormField.validDates, validDates);
    }
  }, [methods, lmnItem, validDates]);

  return (
    <Box pad={{ vertical: 'spacing16' }} gap="spacing16">
      <Box pad={{ horizontal: 'spacing16' }}>
        <Text size="2xl" weight={700} data-testid="lmn_view_title">
          {mode === LMNPopupMode.EDIT
            ? t('Edit Letter of Medical Necessity')
            : t('Add new Letter of Medical Necessity')}
        </Text>
      </Box>

      {isViewLoading ? (
        <Box gap="spacing16">
          <ShimmerBox height="500px" width="100%" />
          <ShimmerBox height="200px" width="100%" />
        </Box>
      ) : (
        <>
          <Box pad="spacing16" background="module" round="container3Round">
            <ChangeFile
              isViewMode={mode === LMNPopupMode.VIEW || mode === LMNPopupMode.EDIT}
              storeKey={LMN_UPLOAD_STORE_KEY}
            />
            <Box margin={{ top: 'spacing24' }}>
              <ScanPreviewContainer
                defaultFiles={allFiles}
                activeFile={0}
                isActiveFileShown
                disableUpload
                isRemovingDisabled
                isShowTooltip={false}
                isThumbsBottom
                storeKey={LMN_UPLOAD_STORE_KEY}
              />
            </Box>
          </Box>

          <Box
            margin={{ top: 'spacing16' }}
            data-testid="lmn_view_form"
            pad="spacing16"
            background="module"
            round="moduleRound"
          >
            <FormProvider {...methods}>
              <DocumentPreviewPopupForm mode={mode} />
            </FormProvider>
          </Box>

          <Box pad={{ horizontal: 'spacing16', bottom: 'spacing16' }} margin={{ top: 'spacing16' }}>
            <Box direction="row" gap="spacing12" width="100%">
              <AppButton
                width="calc(50% - 6px)"
                testId="lmn_view_close"
                buttonType="secondary"
                onClick={handleClose}
              >
                {t('Close')}
              </AppButton>
              {mode === LMNPopupMode.VIEW ? (
                <AppButton
                  width="calc(50% - 6px)"
                  testId="lmn_view_edit"
                  buttonType="primary"
                  onClick={handleEdit}
                >
                  {t('Edit')}
                </AppButton>
              ) : (
                <AppButton
                  width="calc(50% - 6px)"
                  testId="lmn_view_save"
                  buttonType="primary"
                  onClick={methods.handleSubmit(handleSave)}
                  disabled={isLoading}
                >
                  {isLoading ? <Preloader color="white" /> : t('Save')}
                </AppButton>
              )}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default LettersOfMedicalNecessityViewPage;
